import { UNLIMITED_AI_CREDITS_SENTINEL } from "@hex/common";
import React from "react";
import { styled } from "styled-components";

import { Flex, HexProgressBar, Txt } from "../../hex-components";

type BasicCreditMeterProps = {
  usedCredits: number;
  totalCredits: number | typeof UNLIMITED_AI_CREDITS_SENTINEL;
  resetDate: Date;
  remainingCreditsIcon?: React.ReactNode;
};

export const BasicCreditMeter = React.memo(function BasicCreditMeter({
  remainingCreditsIcon,
  resetDate,
  totalCredits,
  usedCredits,
}: BasicCreditMeterProps) {
  const hasUnlimitedCredits = totalCredits === UNLIMITED_AI_CREDITS_SENTINEL;
  const remainingCreditsPercentage =
    totalCredits === 0
      ? 0
      : hasUnlimitedCredits
        ? 1
        : 1 - usedCredits / totalCredits;

  const remainingCreditsString = hasUnlimitedCredits
    ? "Unlimited remaining"
    : `${Math.max(0, totalCredits - usedCredits)}/${totalCredits} remaining`;

  const resetDateString = resetDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

  return (
    <Flex align="stretch" css="width: 100%" direction="column" gap={10}>
      <Flex direction="row" justify="between">
        <Txt>Your credits</Txt>
        <Txt fontColor="muted" fontSize="small">
          Resets {resetDateString}
        </Txt>
      </Flex>
      <HexProgressBar
        $small={true}
        intent="primary"
        value={remainingCreditsPercentage}
      />
      <Flex align="center" direction="row">
        {remainingCreditsIcon && (
          <StyledRemainingCreditsIconContainer>
            {remainingCreditsIcon}
          </StyledRemainingCreditsIconContainer>
        )}
        <Txt fontColor="muted" fontSize="small">
          {remainingCreditsString}
        </Txt>
      </Flex>
    </Flex>
  );
});

const StyledRemainingCreditsIconContainer = styled.div`
  padding-right: 5px;
`;
